import { useEffect, useState } from 'react';
import { Stack, Cluster, Text, Field, Input, Button, Banner, Skeleton } from '../../ui';
import { fetchTiming, saveTiming, type TimingRow } from '../../../lib/notifications-admin';

interface Knob {
  key: keyof TimingRow;
  label: string;
  unit: string;
  min: number;
  max: number;
  hint: string;
}

const KNOBS: Knob[] = [
  { key: 'ending_soon_minutes', label: 'Ending-soon warning', unit: 'min', min: 5, max: 1440, hint: 'How long before a lot closes the "ending soon" alert fires.' },
  { key: 'outbid_coalesce_seconds', label: 'Outbid bundling delay', unit: 'sec', min: 0, max: 3600, hint: 'Quick back-and-forth bids bundle into one outbid alert within this window.' },
  { key: 'reminder_mid_hours', label: 'Payment reminder - second nudge', unit: 'h left', min: 1, max: 168, hint: 'Send the second payment reminder when this many hours remain.' },
  { key: 'reminder_final_hours', label: 'Payment reminder - final nudge', unit: 'h left', min: 1, max: 168, hint: 'Send the final payment reminder when this many hours remain (must be less than the second nudge).' },
];

export function TimingTab() {
  const [timing, setTiming] = useState<TimingRow | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchTiming()
      .then((t) => {
        if (!alive) return;
        setTiming(t);
        setDraft(Object.fromEntries(KNOBS.map((k) => [k.key, String(t[k.key])])));
      })
      .catch((e) => { if (alive) setError(e?.message ?? 'Failed to load timing.'); });
    return () => { alive = false; };
  }, []);

  function validate(): { row: TimingRow | null; fieldError: string | null } {
    const row: Partial<TimingRow> = {};
    for (const k of KNOBS) {
      const n = Number(draft[k.key]);
      if (!Number.isInteger(n) || n < k.min || n > k.max) {
        return { row: null, fieldError: `${k.label} must be a whole number between ${k.min} and ${k.max}.` };
      }
      (row as any)[k.key] = n;
    }
    if ((row.reminder_final_hours as number) >= (row.reminder_mid_hours as number)) {
      return { row: null, fieldError: 'The final payment nudge must come after (fewer hours than) the second nudge.' };
    }
    return { row: row as TimingRow, fieldError: null };
  }

  const { row: validRow, fieldError } = timing ? validate() : { row: null, fieldError: null };
  const dirty = timing ? KNOBS.some((k) => String(timing[k.key]) !== draft[k.key]) : false;

  async function save() {
    const { row } = validate();
    if (!row) return;
    setSaving(true); setError(null); setSaved(false);
    try {
      await saveTiming(row);
      setTiming(row);
      setSaved(true);
    } catch (e: any) {
      setError(e?.message ?? 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  if (error && !timing) return <Banner tone="danger" title="Couldn't load">{error}</Banner>;

  return (
    <Stack gap="group" className="max-w-[640px]">
      <Text role="caption" muted as="p">
        These control WHEN messages fire. Money and forfeiture timing (the 48-hour payment
        window, card-retry backoff) stay in code and are not editable here.
      </Text>

      {error && <Banner tone="danger" title="Couldn't save" onClose={() => setError(null)}>{error}</Banner>}
      {saved && !dirty && <Banner tone="success" title="Saved">Applies to events from now on.</Banner>}

      {!timing ? (
        <Stack gap="group">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[35px] w-full" />)}</Stack>
      ) : (
        <Stack gap="group">
          {KNOBS.map((k) => (
            <Field key={k.key} label={k.label} hint={k.hint}>
              <Cluster gap="tight" className="items-center">
                <Input
                  type="number"
                  inputMode="numeric"
                  min={k.min}
                  max={k.max}
                  value={draft[k.key] ?? ''}
                  onChange={(e) => { setSaved(false); setDraft((d) => ({ ...d, [k.key]: e.target.value })); }}
                  className="w-[110px]"
                />
                <Text role="small" muted as="span">{k.unit}</Text>
              </Cluster>
            </Field>
          ))}

          {fieldError && <Banner tone="warning" title="Check the values">{fieldError}</Banner>}

          <Cluster gap="tight">
            <Button variant="primary" onClick={save} loading={saving} disabled={!dirty || !validRow}>
              Save changes
            </Button>
            {dirty && (
              <Button
                variant="ghost"
                onClick={() => setDraft(Object.fromEntries(KNOBS.map((k) => [k.key, String(timing[k.key])])))}
              >
                Revert
              </Button>
            )}
          </Cluster>
        </Stack>
      )}
    </Stack>
  );
}
